// Three comprehension questions (बोध). Each option is graded on the server; the browser never knows the answer
// beforehand. A wrong option is marked and set aside so the learner can read again and try another.

import type { FC, ReactNode } from 'react'
import type { ReaderStory } from '@/prakrit/components/stories/story-logic'

import { useState } from 'react'
import { Check, CircleHelp, Loader2, Star, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useProgress } from '@/prakrit/lib/client/progress'
import { PrakritText } from '@/prakrit/components/common/prakrit-word'
import { Bi } from '@/prakrit/components/common/bi'
import { SectionTitle, hiDigits } from '@/prakrit/components/grammar/labs/lab-ui'
import { moveBetween } from '@/prakrit/components/lesson/steps/step-ui'
import { postStoryGrade } from '@/prakrit/components/stories/story-logic'

interface QuestionState {
    status: 'idle' | 'checking' | 'correct' | 'wrong' | 'error'
    chosen: string | null
    wrong: string[]
    xp: number
    preview: boolean
    error: string | null
}

const START: QuestionState = { status: 'idle', chosen: null, wrong: [], xp: 0, preview: false, error: null }

/** "+2 XP" for Adults, a star for Kids; honest when nothing was added. */
export const XpNote: FC<{ xp: number, preview: boolean }> = ({ xp, preview }): ReactNode => {
    if (preview) {
        return (
            <span className='text-sm text-muted-foreground'>
                <Bi hi='समीक्षा पूर्वावलोकन: अंक नहीं जुड़ते।' en='Reviewer preview: no XP is added.' layout='inline' />
            </span>
        )
    }

    if (xp <= 0) {
        return (
            <span className='text-sm text-muted-foreground'>
                <Bi hi='इसके अंक आज पहले ही मिल चुके हैं।' en='The XP for this was already counted today.' layout='inline' />
            </span>
        )
    }

    return (
        <span className='inline-flex items-center gap-1.5 rounded-full bg-success-soft px-2.5 py-0.5 text-sm font-semibold text-success'>
            <Star className='kids-only size-4 fill-current' aria-hidden='true' />
            <span lang='en' className='adults-only'>+{xp} XP</span>
            <span lang='hi' className='kids-only hindi'>+{hiDigits(xp)} अंक</span>
        </span>
    )
}

interface QuestionCardProps {
    storyId: string
    index: number
    question: ReaderStory['questions'][number]
    onSolved: (index: number, xp: number) => void
}

const QuestionCard: FC<QuestionCardProps> = ({ storyId, index, question, onSolved }): ReactNode => {
    const [state, setState] = useState<QuestionState>(START)
    const { apply } = useProgress()
    const solved = state.status === 'correct'
    const headingId = `${storyId}-q${index}`

    const choose = async (option: string) => {
        if (solved || state.status === 'checking' || state.wrong.includes(option)) return

        setState(previous => ({ ...previous, status: 'checking', chosen: option, error: null }))

        try {
            const result = await postStoryGrade(storyId, { kind: 'question', index, answer: option })

            if (!result.preview) apply({ receipts: result.receipt ? [result.receipt] : [], attempts: result.attempt ? [result.attempt] : [] })

            setState(previous => ({
                ...previous,
                status: result.correct ? 'correct' : 'wrong',
                wrong: result.correct ? previous.wrong : [...previous.wrong, option],
                xp: result.xpAwarded,
                preview: result.preview
            }))

            if (result.correct) onSolved(index, result.xpAwarded)
        }
        catch (error) {
            setState(previous => ({
                ...previous,
                status: 'error',
                chosen: null,
                error: error instanceof Error ? error.message : 'The answer could not be checked.'
            }))
        }
    }

    return (
        <li className='flex flex-col gap-4 rounded-xl border border-border bg-card p-5 shadow-card in-data-[mode=kids]:border-2'>
            <div className='flex flex-col gap-1.5'>
                <h3 id={headingId} className='flex items-center gap-2 text-sm font-semibold text-saffron-strong'>
                    <CircleHelp className='size-4' aria-hidden='true' />
                    <span lang='hi' className='hindi'>प्रश्न {hiDigits(index + 1)}</span>
                    <span lang='en' className='font-medium text-muted-foreground'>Question {index + 1}</span>
                </h3>
                <p className='pra pra-md leading-[1.7]!'>
                    <PrakritText text={question.pra} />
                </p>
                <Bi hi={question.hi} en={question.en} layout='inline' className='text-[0.9375rem]' enClassName='text-muted-foreground' />
            </div>

            <div
                role='group'
                aria-label={`प्रश्न ${index + 1} के विकल्प · Options for question ${index + 1}`}
                onKeyDown={event => moveBetween(event, '[data-option]:not([disabled])')}
                className='grid gap-2.5 sm:grid-cols-3'
            >
                {question.options.map((option, i) => {
                    const isWrong = state.wrong.includes(option)
                    const isRight = solved && state.chosen === option
                    const checking = state.status === 'checking' && state.chosen === option

                    return (
                        <button
                            key={option}
                            type='button'
                            data-option
                            disabled={isWrong || (solved && !isRight) || (state.status === 'checking' && !checking)}
                            aria-pressed={isRight || checking}
                            onClick={() => choose(option)}
                            className={cn(
                                'flex min-h-tap w-full items-center gap-3 rounded-xl border-2 px-4 py-2.5 text-left shadow-sm transition-colors',
                                isRight ? 'border-success bg-success-soft'
                                    : isWrong ? 'border-danger/50 bg-danger-soft opacity-80'
                                        : checking ? 'border-saffron bg-saffron/10'
                                            : 'border-border bg-card hover:border-saffron/60 hover:bg-saffron/5',
                                solved && !isRight && 'opacity-50',
                                'disabled:cursor-not-allowed'
                            )}
                        >
                            <span
                                aria-hidden='true'
                                className={cn(
                                    'flex size-7 shrink-0 items-center justify-center rounded-full border text-sm font-semibold',
                                    isRight ? 'border-success bg-success text-white dark:text-background'
                                        : isWrong ? 'border-danger bg-danger text-white dark:text-background'
                                            : 'border-border bg-background text-muted-foreground'
                                )}
                            >
                                {isRight ? <Check className='size-4' /> : isWrong ? <X className='size-4' /> : checking ? <Loader2 className='size-4 animate-spin' /> : i + 1}
                            </span>
                            <span lang='pra' className='pra pra-md'>{option}</span>
                            {isWrong && <span className='sr-only'> — सही नहीं · not correct</span>}
                            {isRight && <span className='sr-only'> — सही · correct</span>}
                        </button>
                    )
                })}
            </div>

            <div aria-live='polite' className='min-h-6'>
                {state.status === 'correct' && (
                    <p className='flex flex-wrap items-center gap-2 text-sm font-medium text-success'>
                        <Check className='size-4' aria-hidden='true' />
                        <Bi hi='सही उत्तर! शाबाश।' en='Correct, well done!' layout='inline' enClassName='text-success/80' />
                        <XpNote xp={state.xp} preview={state.preview} />
                    </p>
                )}
                {state.status === 'wrong' && (
                    <p className='flex items-start gap-2 text-sm'>
                        <X className='mt-0.5 size-4 shrink-0 text-danger' aria-hidden='true' />
                        <Bi
                            hi='यह उत्तर नहीं है। कथा को फिर से पढ़िए और दूसरा विकल्प चुनिए।'
                            en='Not this one. Read the story again and choose another option.'
                        />
                    </p>
                )}
                {state.status === 'error' && (
                    <p className='flex items-start gap-2 text-sm text-danger'>
                        <X className='mt-0.5 size-4 shrink-0' aria-hidden='true' />
                        <span className='flex flex-col'>
                            <span lang='hi' className='hindi'>उत्तर की जाँच नहीं हो सकी। कृपया फिर से चुनें।</span>
                            <span lang='en'>{state.error}</span>
                        </span>
                    </p>
                )}
            </div>
        </li>
    )
}

interface StoryQuestionsProps {
    story: ReaderStory
    onSolved: (index: number, xp: number) => void
}

export const StoryQuestions: FC<StoryQuestionsProps> = ({ story, onSolved }): ReactNode => {
    if (story.questions.length === 0) return null

    return (
        <section aria-labelledby='story-questions' className='flex flex-col gap-5'>
            <SectionTitle id='story-questions' hi='प्रश्न' en='Questions'>
                <Bi
                    hi='कथा के आधार पर सही उत्तर चुनिए। हर सही उत्तर पर अंक मिलते हैं।'
                    en='Choose the right answer from the story. Every right answer earns XP.'
                    className='text-[0.9375rem]'
                />
            </SectionTitle>
            <ol className='flex flex-col gap-4'>
                {story.questions.map((question, i) => (
                    <QuestionCard key={i} storyId={story.id} index={i} question={question} onSolved={onSolved} />
                ))}
            </ol>
        </section>
    )
}
